import { useEffect, useState } from 'react';
import { ShieldAlert } from 'lucide-react';
import { DisputeReason, type TransactionSummary } from '@shared/types';

import { Dialog } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { SelectField, TextAreaField } from '@/components/ui/Field';
import { DropZone } from '@/components/ui/DropZone';
import { InlineError } from '@/components/ui/EmptyState';
import { maskedCard, rub } from '@/lib/format';
import { NOUN, plural, pluralize } from '@/lib/plural';

export const STATEMENT_MIN = 40;
export const STATEMENT_MAX = 1000;
export const MAX_EVIDENCE = 5;

/** Display labels only — the wire values in `DisputeReason` stay untouched. */
const REASON_LABELS: Record<DisputeReason, string> = {
  [DisputeReason.NotReceived]: 'Ничего не пришло на карту',
  [DisputeReason.WrongAmount]: 'Пришла другая сумма',
  [DisputeReason.Duplicate]: 'Это дубль другого платежа',
  [DisputeReason.Other]: 'Другая причина',
};

const REASON_ORDER: DisputeReason[] = [
  DisputeReason.NotReceived,
  DisputeReason.WrongAmount,
  DisputeReason.Duplicate,
  DisputeReason.Other,
];

/**
 * Opening a dispute is an accusation an administrator will adjudicate, so the
 * form asks for a real statement and evidence, and says plainly what happens
 * next — including the consequence of a false claim.
 */
export function DisputeDialog({
  transaction,
  open,
  isSubmitting,
  serverError,
  onCancel,
  onSubmit,
}: {
  transaction: TransactionSummary | null;
  open: boolean;
  isSubmitting: boolean;
  serverError: string | null;
  onCancel: () => void;
  onSubmit: (input: { reason: DisputeReason; statement: string; evidence: File[] }) => void;
}) {
  const [reason, setReason] = useState<DisputeReason>(DisputeReason.NotReceived);
  const [statement, setStatement] = useState('');
  const [evidence, setEvidence] = useState<File[]>([]);
  const [showErrors, setShowErrors] = useState(false);

  // Reset whenever a different payment is disputed.
  useEffect(() => {
    if (!open) return;
    setReason(DisputeReason.NotReceived);
    setStatement('');
    setEvidence([]);
    setShowErrors(false);
  }, [open, transaction?.id]);

  if (!transaction) return null;

  const trimmed = statement.trim();
  const statementError =
    trimmed.length === 0
      ? 'Опишите, что произошло.'
      : trimmed.length < STATEMENT_MIN
        ? `Добавьте ещё ${pluralize(STATEMENT_MIN - trimmed.length, NOUN.character)} — администратору нужны детали, чтобы принять решение.`
        : undefined;

  const submit = (): void => {
    if (statementError) {
      setShowErrors(true);
      return;
    }
    onSubmit({ reason, statement: trimmed, evidence });
  };

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      busy={isSubmitting}
      size="lg"
      title="Сообщить, что платёж не пришёл"
      description={
        <>
          <span className="num whitespace-nowrap font-medium text-fg">
            {rub(transaction.amountRub)}
          </span>{' '}
          от {transaction.merchantName} на{' '}
          <span className="num">{maskedCard(transaction.cardLast4)}</span> · номер{' '}
          {/* References run to twenty-odd characters with no natural break;
              without this the dialog header pushes the sheet sideways. */}
          <span className="num break-all">{transaction.reference}</span>
        </>
      }
      footer={
        <>
          <Button onClick={onCancel} disabled={isSubmitting}>
            Отмена
          </Button>
          <Button variant="danger" onClick={submit} isLoading={isSubmitting}>
            {isSubmitting ? 'Отправляю…' : 'Отправить'}
          </Button>
        </>
      }
    >
      {serverError ? <InlineError message={serverError} className="mb-4" /> : null}

      <div className="space-y-4">
        <SelectField
          label="Что произошло"
          required
          value={reason}
          onChange={(event) => setReason(event.target.value as DisputeReason)}
          disabled={isSubmitting}
        >
          {REASON_ORDER.map((value) => (
            <option key={value} value={value}>
              {REASON_LABELS[value]}
            </option>
          ))}
        </SelectField>

        <TextAreaField
          label="Ваше объяснение"
          required
          rows={5}
          maxLength={STATEMENT_MAX}
          value={statement}
          disabled={isSubmitting}
          onChange={(event) => setStatement(event.target.value)}
          error={showErrors ? statementError : undefined}
          hint={
            statementError && !showErrors
              ? `Минимум ${pluralize(STATEMENT_MIN, NOUN.character)}. Укажите, во сколько вы проверяли счёт и что показал банк.`
              : `${trimmed.length} из ${STATEMENT_MAX} ${plural(STATEMENT_MAX, NOUN.character)}.`
          }
          placeholder="Проверил карту в 14:20 и ещё раз в 14:45. Входящего перевода на эту сумму не было, баланс не изменился."
        />

        <div>
          <p className="mb-1.5 text-sm font-medium text-fg-secondary">
            Подтверждения{' '}
            <span className="font-normal text-fg-muted">
              (необязательно, но спор без скриншота рассматривается заметно дольше)
            </span>
          </p>
          <DropZone
            files={evidence}
            onChange={setEvidence}
            maxFiles={MAX_EVIDENCE}
            disabled={isSubmitting}
            // A trader disputing a payment is usually holding a second phone or
            // photographing the banking app on the same one; each shot appends
            // to the list, so several can be taken in a row.
            capture="environment"
            label="Скриншоты выписки из банка"
            requirements="Должны быть видны счёт, период и баланс. Всё лишнее обрежьте."
          />
        </div>

        <div className="flex gap-3 rounded-md border border-warn-500 bg-warn-50 px-3 py-3">
          <ShieldAlert
            size={15}
            strokeWidth={1.5}
            aria-hidden="true"
            className="mt-0.5 shrink-0 text-warn-700"
          />
          <div className="text-sm leading-normal text-warn-700">
            <p className="font-medium">Заявку рассмотрит администратор.</p>
            <p className="mt-1">
              Платёж заморожен, пока он сверяет данные мерчанта с вашими. Если деньги всё же пришли,
              а вы заявили обратное, транзакция всё равно будет выплачена, а к аккаунту применят
              санкции. За повторные ложные заявки аккаунт блокируют.
            </p>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
